import { Component, OnInit } from '@angular/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { AccessControlService, CapabilityDTO } from 'src/app/services/access-control.service';
import { ToastService } from 'src/app/services/toast.service';
import { StorageUtil } from 'src/app/utils/storage.util';
import Swal from 'sweetalert2';

interface CategoryGroup {
  name: string;
  caps: CapabilityDTO[];
}

/**
 * EAS-198 — atribuição de permissões de ação por usuário/marina.
 * Perfil Admin/Visualização (presets) ou Personalizado (capacidades por área).
 */
@Component({
  selector: 'app-user-access',
  templateUrl: './user-access.component.html',
  styleUrls: ['./user-access.component.scss']
})
export class UserAccessComponent implements OnInit {

  userId: number;
  marinaId: number;
  userName = '';

  profile: 'ADMIN' | 'VIEWER' | 'CUSTOM' = 'VIEWER';
  categories: CategoryGroup[] = [];
  selected: { [code: string]: boolean } = {};

  constructor(
    public ref: DynamicDialogRef,
    private config: DynamicDialogConfig,
    private spinner: NgxSpinnerService,
    private accessService: AccessControlService,
    private toasty: ToastService
  ) {}

  ngOnInit(): void {
    const user = this.config.data ? this.config.data.user : null;
    this.userId = user ? user.id : null;
    this.userName = user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : '';
    this.marinaId = StorageUtil.getMarinaId();
    this.load();
  }

  private load(): void {
    this.spinner.show();
    this.accessService.getCatalog().subscribe({
      next: (catalog) => {
        this.categories = this.groupByCategory(catalog);
        catalog.forEach((c) => (this.selected[c.code] = false));
        this.accessService.getUserAccess(this.userId, this.marinaId).subscribe({
          next: (access) => {
            this.profile = access.profile || 'VIEWER';
            (access.capabilityCodes || []).forEach((code) => (this.selected[code] = true));
            this.spinner.hide();
          },
          error: () => this.spinner.hide()
        });
      },
      error: () => {
        this.spinner.hide();
        Swal.fire('Atenção', 'Não foi possível carregar as permissões.', 'warning');
      }
    });
  }

  private groupByCategory(catalog: CapabilityDTO[]): CategoryGroup[] {
    const map = new Map<string, CapabilityDTO[]>();
    catalog.forEach((c) => {
      if (!map.has(c.category)) {
        map.set(c.category, []);
      }
      map.get(c.category).push(c);
    });
    return Array.from(map.entries()).map(([name, caps]) => ({ name, caps }));
  }

  get isCustom(): boolean {
    return this.profile === 'CUSTOM';
  }

  toggleCategory(group: CategoryGroup, value: boolean): void {
    group.caps.forEach((c) => (this.selected[c.code] = value));
  }

  save(): void {
    const capabilityCodes = this.profile === 'CUSTOM'
      ? Object.keys(this.selected).filter((code) => this.selected[code])
      : [];
    this.spinner.show();
    this.accessService.setUserAccess(this.userId, this.marinaId, { profile: this.profile, capabilityCodes })
      .subscribe({
        next: () => {
          this.spinner.hide();
          this.toasty.success('Permissões salvas com sucesso!');
          this.ref.close(true);
        },
        error: (error) => {
          this.spinner.hide();
          const msg = error && error.error && error.error.message ? error.error.message : 'Falha ao salvar permissões.';
          Swal.fire('Atenção', msg, 'warning');
        }
      });
  }
}
